import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { useFetch } from '../../hooks/useFetch';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DirectorPanelState from './DirectorPanelState';
import { useConfirm } from '../ui/ConfirmDialog';
import { formatTimestamp } from '../../utils/formatDate';
import { buildQuery } from '../../utils/buildQuery';

const ALL_DIRECTOR_LOCATIONS = 'ALL';
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'linked_event', label: 'Linked Events' },
  { value: 'review_needed', label: 'Review Needed' },
  { value: 'unmatched', label: 'Unmatched' }
];

function DirectorSmsCalloutPanel({ selectedLocationCode = ALL_DIRECTOR_LOCATIONS }) {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dismissingId, setDismissingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkDismissing, setIsBulkDismissing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const messagesUrl = useMemo(() => {
    const qs = buildQuery({
      limit: 100,
      locationCode: selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null,
      processingStatus: statusFilter !== 'all' ? statusFilter : null
    });
    return `/api/integrations/eztexting/messages?${qs}`;
  }, [selectedLocationCode, statusFilter]);

  const { data: fetchedRows, isLoading, error: fetchError } = useFetch(messagesUrl);

  useEffect(() => {
    if (fetchedRows) {
      setRows(Array.isArray(fetchedRows) ? fetchedRows : []);
      setSelectedIds([]);
      setErrorMessage('');
    }
    if (fetchError) {
      setErrorMessage(fetchError);
    }
  }, [fetchedRows, fetchError]);

  const summary = useMemo(() => ({
    linked: rows.filter(row => row.processingStatus === 'linked_event').length,
    reviewNeeded: rows.filter(row => row.processingStatus === 'review_needed').length,
    unmatched: rows.filter(row => row.processingStatus === 'unmatched').length
  }), [rows]);

  const dismissibleRows = useMemo(
    () => rows.filter(row => !row.linkedEventId && (row.processingStatus === 'unmatched' || row.processingStatus === 'review_needed')),
    [rows]
  );

  const allDismissibleSelected = dismissibleRows.length > 0 && dismissibleRows.every(row => selectedIds.includes(row.id));

  const handleDismiss = async (row) => {
    const confirmed = await confirm(
      'Dismiss this SMS intake record from active callout review? Linked attendance events will be preserved and cannot be dismissed here.',
      { title: 'Dismiss SMS record', confirmLabel: 'Dismiss', tone: 'danger' }
    );
    if (!confirmed) {
      return;
    }

    setDismissingId(row.id);
    setErrorMessage('');
    try {
      await apiRequest(`/api/integrations/eztexting/messages/${row.id}/dismiss`, {
        method: 'PATCH'
      });
      setRows(current => current.filter(item => item.id !== row.id));
    } catch (error) {
      setErrorMessage(error.message || 'Unable to dismiss SMS intake record.');
    } finally {
      setDismissingId(null);
    }
  };

  const handleToggleSelected = (rowId) => {
    setSelectedIds(current => (
      current.includes(rowId)
        ? current.filter(id => id !== rowId)
        : [...current, rowId]
    ));
  };

  const handleToggleSelectAll = () => {
    if (allDismissibleSelected) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(dismissibleRows.map(row => row.id));
  };

  const handleDismissSelected = async () => {
    if (!selectedIds.length) {
      return;
    }

    const confirmed = await confirm(
      `Dismiss ${selectedIds.length} SMS intake record${selectedIds.length === 1 ? '' : 's'} from active callout review? Linked attendance events are not included.`,
      { title: 'Dismiss selected records', confirmLabel: 'Dismiss', tone: 'danger' }
    );
    if (!confirmed) {
      return;
    }

    setIsBulkDismissing(true);
    setErrorMessage('');
    try {
      const result = await apiRequest('/api/integrations/eztexting/messages/dismiss', {
        method: 'POST',
        body: JSON.stringify({ ids: selectedIds })
      });
      const dismissedIds = Array.isArray(result?.dismissedIds) ? result.dismissedIds : [];
      setRows(current => current.filter(item => !dismissedIds.includes(item.id)));
      setSelectedIds([]);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to dismiss selected SMS intake records.');
    } finally {
      setIsBulkDismissing(false);
    }
  };

  return (
    <section className="role-section director-callout-history-section">
      <div className="role-section-header">
        <h2>Callout History</h2>
        <div className="role-section-actions">
          <span>{rows.length} shown</span>
        </div>
      </div>
      <p className="role-section-copy">
        Review inbound SMS callouts, unmatched senders, and review-needed items without replacing manual attendance entry.
      </p>
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading SMS callout history...' : ''} />
      <DirectorPanelState kind="error" message={errorMessage} />

      <div className="role-request-summary-row">
        <div className="role-request-summary-card">
          <span>Linked Events</span>
          <strong>{summary.linked}</strong>
        </div>
        <div className="role-request-summary-card">
          <span>Review Needed</span>
          <strong>{summary.reviewNeeded}</strong>
        </div>
        <div className="role-request-summary-card">
          <span>Unmatched</span>
          <strong>{summary.unmatched}</strong>
        </div>
      </div>

      <div className="director-toolbar">
        <div className="director-toolbar-group">
          <label>Callout Filter</label>
          <div className="director-filter-chip-row">
            {STATUS_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                className={`director-filter-chip ${statusFilter === option.value ? 'active' : ''}`}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="director-toolbar-group director-callout-bulk-actions">
          <label>Bulk Actions</label>
          <div className="director-filter-chip-row">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleDismissSelected}
              disabled={!selectedIds.length}
              loading={isBulkDismissing}
              loadingLabel="Dismissing..."
            >
              {`Dismiss Selected${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
            </Button>
          </div>
        </div>
      </div>

      <div className="table-card">
        <table className="role-table director-callout-history-table">
          <thead>
            <tr>
              <th className="director-callout-col-select">
                <input
                  type="checkbox"
                  checked={allDismissibleSelected}
                  onChange={handleToggleSelectAll}
                  disabled={!dismissibleRows.length}
                  aria-label="Select all dismissible SMS records"
                />
              </th>
              <th className="director-callout-col-time">Received</th>
              <th className="director-callout-col-phone">From</th>
              <th className="director-callout-col-classification">Classification</th>
              <th className="director-callout-col-manager">Matched Manager</th>
              <th className="director-callout-col-status">Status</th>
              <th>Message</th>
              <th className="director-callout-col-linked">Linked Event</th>
              <th className="director-callout-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id}>
                <td className="director-callout-col-select">
                  {!row.linkedEventId && (row.processingStatus === 'unmatched' || row.processingStatus === 'review_needed') ? (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => handleToggleSelected(row.id)}
                      aria-label={`Select SMS record ${row.id}`}
                    />
                  ) : null}
                </td>
                <td className="director-callout-col-time">{formatTimestamp(row.receivedAt)}</td>
                <td className="director-callout-col-phone">{row.fromNumber}</td>
                <td className="director-callout-col-classification">{row.classification || 'unknown'}</td>
                <td className="director-callout-col-manager">
                  {row.managerName ? `${row.managerName} (${row.managerLocationCode || 'N/A'})` : 'No match'}
                </td>
                <td className="director-callout-col-status">
                  <StatusPill tone={row.processingStatus === 'linked_event' ? 'active' : 'inactive'}>
                    {row.processingStatus.replace(/_/g, ' ')}
                  </StatusPill>
                </td>
                <td>{row.messageBody}</td>
                <td className="director-callout-col-linked">
                  {row.linkedEventId
                    ? `${row.eventType || 'event'} · ${row.eventStartDate || ''}`.trim()
                    : '-'}
                </td>
                <td className="director-callout-col-actions">
                  {!row.linkedEventId && (row.processingStatus === 'unmatched' || row.processingStatus === 'review_needed') ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={dismissingId === row.id}
                      loadingLabel="Dismissing..."
                      onClick={() => handleDismiss(row)}
                    >
                      Dismiss
                    </Button>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && !isLoading && (
              <tr>
                <td colSpan="9">No inbound SMS callouts are available for this scope yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default DirectorSmsCalloutPanel;
